/* eslint-disable react/prop-types */
import { useState } from 'react';
import { AlertTriangle, ArrowRightLeft, ChevronDown, CloudSun, History, Phone, PlaneLanding, PlaneTakeoff, Radio } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCityName } from '@/components/airportUtils';
import { BRAND_NESTED_PANEL } from '@/constants/brandColors';
import {
  STATUS_TONE_CLASSES,
  clockAt,
  codesharesLine,
  connectionLine,
  dayAt,
  dayTone,
  flightEnd,
  flightFactsLine,
  forecastLine,
  nextLineParts,
  gateLine,
  positionLine,
  statusHeadline,
  trackRecordFacts,
  trackRecordHeadline,
  updatedAgo,
  weatherLine,
} from '@/utils/flightStatusDisplay';
import FlightMap from './FlightMap';
import PredictionsTag from './PredictionsTag';

/**
 * Live status and insights for the flights of one leg, on the trip page
 * (FlightDetailsModal → Flight Details). Data: src/hooks/useFlightLive.js.
 * Renders nothing when there's nothing to say.
 */

function Pill({ tone, children }) {
  const t = STATUS_TONE_CLASSES[tone] || STATUS_TONE_CLASSES.muted;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 sm:px-2.5 sm:py-1 text-[11px] sm:text-[12px] font-medium whitespace-nowrap', t.pill)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', t.dot)} aria-hidden="true" />
      {children}
    </span>
  );
}

function End({ label, icon: Icon, end }) {
  if (!end) return null;
  const gates = gateLine(end);
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-widest text-white/45">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {label}
      </div>
      {/* Each time stays on one line; on a narrow phone the old time drops
          below the new one rather than "12:37" and "PM" splitting. */}
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="whitespace-nowrap text-[16px] sm:text-[17px] text-white tabular-nums">{end.time || '—'}</span>
        {end.scheduledTime && <span className="whitespace-nowrap text-[11px] sm:text-[12px] text-white/40 line-through tabular-nums">{end.scheduledTime}</span>}
      </div>
      {gates && <div className="mt-0.5 text-[11px] sm:text-[12px] text-white/70">{gates}</div>}
      {end.baggage && <div className="mt-0.5 text-[11px] sm:text-[12px] text-white/70">Bags: carousel {end.baggage}</div>}
    </div>
  );
}

/** How far along an en-route flight is, from its own times. */
function progressOf(status, nowMs) {
  const off = Date.parse(status?.actual_out || '');
  const on = Date.parse(status?.estimated_in || status?.scheduled_in || '');
  if (!Number.isFinite(off) || !Number.isFinite(on) || on <= off) return null;
  return Math.min(100, Math.max(0, Math.round(((nowMs - off) / (on - off)) * 100)));
}

function AirlineContact({ airline }) {
  if (!airline || (!airline.phone && !airline.url)) return null;
  let site = null;
  try {
    site = airline.url ? new URL(airline.url).hostname.replace(/^www\./, '') : null;
  } catch {
    site = null;
  }
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] sm:text-[13px] text-white/80">
      <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-white/45" aria-hidden="true" />
      <span className="text-white/90">{airline.name || 'The airline'}:</span>
      {airline.phone && <a href={`tel:${airline.phone.replace(/[^+\d]/g, '')}`} className="underline underline-offset-2 hover:text-white">{airline.phone}</a>}
      {airline.phone && site && <span className="text-white/35">·</span>}
      {site && <a href={airline.url.replace(/^http:/, 'https:')} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-white">{site}</a>}
    </p>
  );
}

/** "UA 2210 · leaves 9:05 PM · Gate C17", wrapping only between pieces. */
function NextParts({ next }) {
  return nextLineParts(next).map((part, i) => (
    <span key={part}>{i > 0 && ' · '}<span className="whitespace-nowrap">{part}</span></span>
  ));
}

/** "Likely to leave around 8:45 PM or later": the plane for the flight is coming in late. */
function ForecastLine({ seg }) {
  const line = forecastLine(seg.forecast, seg.status, getCityName);
  if (!line) return null;
  return (
    <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2.5 text-[12px] sm:text-[13px] leading-snug sm:leading-relaxed">
      <PredictionsTag />
      <span className="text-amber-100">{line.label}.</span> <span className="text-white/75">{line.text}</span>
    </div>
  );
}

/** The time to make the next flight, and where it leaves from; tagged once it's a warning. */
function ConnectionLine({ connection }) {
  const line = connectionLine(connection, getCityName);
  if (!line) return null;
  const tone = STATUS_TONE_CLASSES[line.tone] || STATUS_TONE_CLASSES.muted;
  const warning = Boolean(connection.level);
  return (
    <div className={cn('text-[12px] sm:text-[13px] leading-snug sm:leading-relaxed', warning && 'rounded-xl border px-3 py-2.5', warning && (line.tone === 'bad' ? 'border-rose-400/25 bg-rose-400/[0.06]' : 'border-amber-400/20 bg-amber-400/[0.06]'))}>
      {warning && <PredictionsTag />}
      <div className="flex items-start gap-2 sm:gap-2.5">
        <span className={cn('mt-[0.4rem] h-1.5 w-1.5 flex-shrink-0 rounded-full', tone.dot)} aria-hidden="true" />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-white/90">
            <ArrowRightLeft className="h-3.5 w-3.5 text-white/45" aria-hidden="true" />
            {line.label}
          </span>
          <span className="block text-white/75">{line.text}</span>
          {line.next && <span className="block text-[11px] sm:text-[12px] text-white/55">Next: <NextParts next={line.next} /></span>}
        </span>
      </div>
    </div>
  );
}

function LiveBlock({ seg, insight, nowMs }) {
  const status = seg.status;
  const headline = statusHeadline(status);
  const dep = flightEnd(status, 'departure');
  const arr = flightEnd(status, 'arrival');
  const inTheAir = status.state === 'departed';
  const progress = inTheAir && !insight?.position ? progressOf(status, nowMs) : null;
  const facts = flightFactsLine(status.details);
  const codeshares = codesharesLine(status.details);
  const altitude = positionLine(insight?.position);
  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Pill tone={headline.tone}>{headline.label}</Pill>
        <span className="text-[10px] sm:text-[11px] text-white/40">{updatedAgo(seg.checked_at, nowMs)}</span>
      </div>
      {status.state === 'cancelled' ? (
        <div className="space-y-2.5">
          <p className="text-[12px] sm:text-[13px] text-white/80 leading-relaxed">
            The airline has cancelled this flight. Airlines usually offer another flight; check the airline’s app for your options, or message us from this trip and we’ll help.
          </p>
          <AirlineContact airline={insight?.airline} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <End label="Departure" icon={PlaneTakeoff} end={dep} />
          <End label="Arrival" icon={PlaneLanding} end={arr} />
        </div>
      )}
      <ForecastLine seg={seg} />
      <ConnectionLine connection={seg.connection} />
      {inTheAir && insight?.position && (
        <div className="space-y-1.5">
          <FlightMap position={insight.position} status={status} origin={seg.origin} destination={seg.destination} nowMs={nowMs} />
          {altitude && <p className="text-center text-[11px] sm:text-[12px] text-white/60">{altitude}</p>}
        </div>
      )}
      {progress != null && (
        <div className="h-1.5 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Flight progress">
          <div className="h-full rounded-full bg-gradient-to-r from-sky-400/80 to-brand-300/80" style={{ width: `${progress}%` }} />
        </div>
      )}
      {status.state === 'diverted' && <AirlineContact airline={insight?.airline} />}
      {(facts || codeshares) && (
        <div className="space-y-0.5 text-[11px] sm:text-[12px] text-white/55">
          {facts && <p>{facts}</p>}
          {codeshares && <p>{codeshares}</p>}
        </div>
      )}
    </div>
  );
}

function InboundLine({ inbound, departureIso, departureTz }) {
  if (!inbound) return null;
  const atIso = inbound.actual_in || inbound.estimated_in || inbound.scheduled_in;
  const time = clockAt(atIso, inbound.timezone);
  // The plane often arrives the evening before an early flight: name the day then.
  const sameDay = dayAt(atIso, inbound.timezone) === dayAt(departureIso, departureTz);
  const when = time ? (sameDay ? `at ${time}` : `${dayAt(atIso, inbound.timezone)} at ${time}`) : null;
  const from = inbound.from_city ? `${inbound.from_city} (${inbound.from})` : inbound.from;
  const where = {
    scheduled: `is due in from ${from}${when ? ` ${when}` : ''}`,
    en_route: `is flying in from ${from}${when ? ` and lands ${when}` : ''}`,
    landed: `has landed from ${from}`,
    arrived: `is at the gate, in from ${from}`,
    cancelled: `was coming from ${from}; that flight is cancelled, so the airline may bring in another plane`,
    diverted: `was coming from ${from} and has been diverted`,
  }[inbound.state] || `is coming from ${from}`;
  const late = Number(inbound.arrival_delay_minutes) >= 15 && ['scheduled', 'en_route'].includes(inbound.state);
  return (
    <div className="flex items-start gap-2 sm:gap-2.5 text-[12px] sm:text-[13px] text-white/80 leading-snug sm:leading-relaxed">
      <Radio className="mt-0.5 h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-sky-300" aria-hidden="true" />
      <span>
        <span className="text-white/90">Your plane</span> {where}.
        {late && <span className="text-amber-200"> It’s running about {inbound.arrival_delay_minutes} min late.</span>}
      </span>
    </div>
  );
}

function AirportLines({ airports }) {
  const items = Object.values(airports || {}).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="space-y-1.5">
      {items.map((a) => (
        <div key={a.airport} className="flex items-start gap-2 sm:gap-2.5 text-[12px] sm:text-[13px] text-white/80 leading-snug sm:leading-relaxed">
          <AlertTriangle className={cn('mt-0.5 h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0', a.color === 'red' ? 'text-rose-300' : 'text-amber-300')} aria-hidden="true" />
          <span>
            <span className="text-white/90">Delays at {String(a.airport || '').replace(/^K(?=[A-Z]{3}$)/, '')}:</span>{' '}
            {(a.reasons || []).map((r) => r.reason).join('; ') || `about ${a.delay_minutes} min`}
          </span>
        </div>
      ))}
    </div>
  );
}

function WeatherLines({ weather, origin, destination }) {
  const rows = [['origin', origin], ['destination', destination]]
    .map(([end, code]) => ({ code, line: weatherLine(weather?.[end]) }))
    .filter((r) => r.line);
  if (!rows.length) return null;
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <p key={r.code} className="flex items-start gap-2 sm:gap-2.5 text-[12px] sm:text-[13px] text-white/80 leading-snug">
          <CloudSun className="mt-0.5 h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-white/45" aria-hidden="true" />
          <span><span className="text-white/90">{r.code} now:</span> {r.line}</span>
        </p>
      ))}
    </div>
  );
}

function TrackRecord({ record }) {
  if (!record) return null;
  const days = [...(record.days || [])].reverse(); // oldest → newest
  const facts = trackRecordFacts(record);
  return (
    <div className="space-y-2 sm:space-y-2.5">
      <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-widest text-white/45">
        <History className="h-3.5 w-3.5" aria-hidden="true" /> Track record · {record.flight}
      </div>
      <p className="text-[13px] sm:text-[14px] text-white/90">{trackRecordHeadline(record)}</p>
      <div className="flex items-end gap-1" aria-hidden="true">
        {days.map((d) => {
          const tone = STATUS_TONE_CLASSES[dayTone(d)];
          const late = Math.max(0, Number(d.arrival_delay_minutes) || 0);
          const height = d.status === 'cancelled' ? 6 : 10 + Math.min(22, late / 3);
          return (
            <span
              key={d.date}
              title={`${d.date}: ${d.status === 'cancelled' ? 'cancelled' : late ? `${late} min late` : 'on time'}`}
              className={cn('w-2.5 sm:w-3 rounded-sm opacity-90', tone.dot)}
              style={{ height }}
            />
          );
        })}
      </div>
      {facts.length > 0 && <p className="text-[11px] sm:text-[12px] text-white/55 leading-snug">{facts.join(' · ')}</p>}
      <p className="text-[10px] sm:text-[11px] text-white/35 leading-snug">How this flight number has run over the last {record.flights} flights. It isn’t a prediction for your date.</p>
    </div>
  );
}

function Timeline({ timeline, timezone }) {
  const [open, setOpen] = useState(false);
  if (!timeline?.length) return null;
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 text-[11px] sm:text-[12px] text-white/60 hover:text-white/85">
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden="true" />
        What changed ({timeline.length})
      </button>
      {open && (
        <ol className="mt-2 space-y-2 border-l border-white/10 pl-3">
          {[...timeline].reverse().map((e) => (
            <li key={`${e.kind}-${e.at}`} className="text-[11px] sm:text-[12px] leading-snug sm:leading-relaxed">
              <span className="text-white/40 tabular-nums">{clockAt(e.at, timezone) || ''}</span>{' '}
              <span className="text-white/85">{e.title}.</span> <span className="text-white/55">{e.body}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function FlightLivePanel({ statusSegments = [], insightSegments = [], nowMs = Date.now() }) {
  const keys = new Set([...statusSegments, ...insightSegments].map((s) => `${s.slice_index}:${s.segment_index}`));
  const rows = [...keys].sort().map((k) => ({
    status: statusSegments.find((s) => `${s.slice_index}:${s.segment_index}` === k) || null,
    insight: insightSegments.find((s) => `${s.slice_index}:${s.segment_index}` === k) || null,
  }));
  // A card only once there's real data for the flight (founder 2026-11-02):
  // no "Live status starts …" or "isn't available" placeholders. Every trip
  // without data shows nothing, so ticket status stays discretionary.
  const visible = rows.filter(({ status, insight }) => status?.status || insight?.track_record || insight?.timeline?.length);
  if (!visible.length) return null;

  return (
    <div className="space-y-3">
      {visible.map(({ status, insight }) => {
        const seg = status || insight;
        const live = status?.status ? status : null;
        const tz = live?.status?.origin_timezone || null;
        return (
          <div key={`${seg.slice_index}:${seg.segment_index}`} className={cn(BRAND_NESTED_PANEL, 'space-y-3 p-3.5 sm:space-y-4 sm:p-5')}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] sm:text-[13px] text-white/85 font-medium">{seg.flight} · {seg.origin} → {seg.destination}</span>
              <span className="text-[10px] sm:text-[11px] uppercase tracking-widest text-white/40">Flight status</span>
            </div>
            {live && <LiveBlock seg={live} insight={insight} nowMs={nowMs} />}
            {/* The forecast already says where the plane is. */}
            {live && !live.forecast && <InboundLine inbound={insight?.inbound} departureIso={live.status.scheduled_out} departureTz={live.status.origin_timezone} />}
            {live && <AirportLines airports={insight?.airports} />}
            {live && <WeatherLines weather={insight?.weather} origin={seg.origin} destination={seg.destination} />}
            {insight?.track_record && <div className="border-t border-white/[0.06] pt-3 sm:pt-4"><TrackRecord record={insight.track_record} /></div>}
            <Timeline timeline={insight?.timeline} timezone={tz} />
          </div>
        );
      })}
    </div>
  );
}
